import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Briefcase } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { analyticsAPI } from '../../../utils/api';
import { getErrorMessage } from '../../../utils/errorTranslation';

const OffersMetrics = ({ filters, showToast }) => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await analyticsAPI.getOffersMetrics({ period: filters.period });
      setData(response.data?.data || null);
    } catch (error) {
      console.error('Error fetching offers metrics:', error);
      if (showToast) {
        showToast(getErrorMessage(error, t, t('dashboard.analytics.error.fetchFailed')), 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [filters.period, showToast, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 h-full min-h-[320px]"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.analytics.offers.title')}</h3>
          <p className="text-sm text-gray-500">{t('dashboard.analytics.offers.subtitle')}</p>
        </div>
        <Briefcase className="h-6 w-6 text-primary-600" />
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">{t('common.loading')}</div>
      ) : !data ? (
        <div className="text-sm text-gray-500">{t('dashboard.analytics.noData')}</div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500">{t('dashboard.analytics.offers.active')}</div>
              <div className="text-lg font-semibold text-gray-900">{data.active || 0}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500">{t('dashboard.analytics.offers.closed')}</div>
              <div className="text-lg font-semibold text-gray-900">{data.closed || 0}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500">{t('dashboard.analytics.offers.cancelled')}</div>
              <div className="text-lg font-semibold text-gray-900">{data.cancelled || 0}</div>
            </div>
          </div>

          {Array.isArray(data.popularOffers) && data.popularOffers.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">{t('dashboard.analytics.offers.popular')}</h4>
              <div className="space-y-2">
                {data.popularOffers.map(offer => (
                  <div key={offer.id} className="flex items-center justify-between text-sm text-gray-600">
                    <span className="truncate mr-3">{offer.title}</span>
                    <span className="text-xs text-gray-500">
                      {offer.applications} {t('dashboard.analytics.offers.applications')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default OffersMetrics;
